import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GetRequest, PostRequest } from 'components/common';

const GoogleSheetsPicker = ({ dataSourceId, onSpreadsheetsPicked, onCancel }) => {
  const [pickerConfig, setPickerConfig] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [gsiLoaded, setGsiLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenClientRef = useRef(null);
  const pickerShownRef = useRef(false);

  // Fetch picker configuration from backend
  useEffect(() => {
    GetRequest({
      url: '/quote-builder/data-sources/google-sheets/picker-config',
      callback: (data) => {
        setPickerConfig(data);
        if (!data.api_key || !data.client_id) {
          setError('Google Picker is not configured. Please contact support.');
          setIsLoading(false);
        }
      },
      401: () => {
        setError('Not authorized. Please log in again.');
        setIsLoading(false);
      },
      422: () => {
        setError('Failed to load picker configuration');
        setIsLoading(false);
      }
    });
  }, []);

  // Load Google API script
  useEffect(() => {
    const loadGapiScript = () => {
      if (window.gapi && window.gapi.load) {
        window.gapi.load('picker', () => setPickerLoaded(true));
        return;
      }

      const existingScript = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          window.gapi.load('picker', () => setPickerLoaded(true));
        });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.gapi.load('picker', () => setPickerLoaded(true));
      };
      script.onerror = () => {
        setError('Failed to load Google API');
        setIsLoading(false);
      };
      document.body.appendChild(script);
    };

    loadGapiScript();
  }, []);

  // Load Google Identity Services script
  useEffect(() => {
    const loadGsiScript = () => {
      if (window.google && window.google.accounts) {
        setGsiLoaded(true);
        return;
      }

      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => setGsiLoaded(true));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setGsiLoaded(true);
      script.onerror = () => {
        setError('Failed to load Google Identity Services');
        setIsLoading(false);
      };
      document.body.appendChild(script);
    };

    loadGsiScript();
  }, []);

  const showPicker = useCallback(() => {
    if (!pickerLoaded || !accessToken || !pickerConfig || pickerShownRef.current) return;

    pickerShownRef.current = true;
    setIsLoading(false);

    // Create a view for Google Sheets only
    const view = new google.picker.DocsView(google.picker.ViewId.SPREADSHEETS);
    view.setMimeTypes('application/vnd.google-apps.spreadsheet');
    view.setMode(google.picker.DocsViewMode.LIST);

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(accessToken)
      .setDeveloperKey(pickerConfig.api_key)
      .setAppId(pickerConfig.app_id)
      .setTitle('Select spreadsheets to use')
      .setCallback((data) => {
        if (data.action === google.picker.Action.PICKED) {
          const documents = data.docs || [];
          const spreadsheets = documents.map(doc => ({
            id: doc.id,
            name: doc.name,
            url: doc.url
          }));

          // Register picked spreadsheets with backend
          PostRequest({
            url: `/quote-builder/data-sources/${dataSourceId}/pick-spreadsheets`,
            data: { spreadsheets },
            callback: (response) => {
              onSpreadsheetsPicked(response.all_spreadsheets || []);
            },
            422: (response) => {
              setError(`Failed to register spreadsheets: ${response.error || 'Unknown error'}`);
            }
          });
        } else if (data.action === google.picker.Action.CANCEL) {
          onCancel?.();
        }
      })
      .build();

    picker.setVisible(true);
  }, [pickerLoaded, accessToken, pickerConfig, dataSourceId, onSpreadsheetsPicked, onCancel]);

  // Initialize token client when GSI is loaded
  useEffect(() => {
    if (!gsiLoaded || !pickerConfig || tokenClientRef.current) return;

    tokenClientRef.current = google.accounts.oauth2.initTokenClient({
      client_id: pickerConfig.client_id,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: (response) => {
        if (response.error) {
          setError(`Authentication failed: ${response.error}`);
          setIsLoading(false);
          return;
        }
        if (response.access_token) {
          setAccessToken(response.access_token);
        }
      }
    });

    // Request token
    tokenClientRef.current.requestAccessToken({ prompt: '' });
  }, [gsiLoaded, pickerConfig]);

  // Show picker when token is obtained
  useEffect(() => {
    if (accessToken && pickerLoaded && pickerConfig) {
      showPicker();
    }
  }, [accessToken, pickerLoaded, pickerConfig, showPicker]);

  if (error) {
    return (
      <div className="google-sheets-picker-error" style={{ padding: '10px', color: '#dc3545' }}>
        {error}
        <button onClick={onCancel} style={{ marginLeft: '10px' }}>Close</button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="google-sheets-picker-loading" style={{ padding: '10px', color: '#666' }}>
        Loading Google Picker...
      </div>
    );
  }

  return null;
};

export default GoogleSheetsPicker;
